import React from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "pwa_install_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export function InstallPrompt() {
  const [deferred, setDeferred] = React.useState(null);
  const [show, setShow] = React.useState(false);
  const [iosHint, setIosHint] = React.useState(false);

  React.useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1") return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setShow(false);
      setIosHint(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show manual instructions instead.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => { setIosHint(true); setShow(true); }, 1200);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") localStorage.setItem(DISMISS_KEY, "1");
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md"
    >
      <div className="bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700 p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-amber-400/15 flex items-center justify-center shrink-0">
          <Download className="h-5 w-5 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-sm">Install Diamond Sharp</div>
          {iosHint ? (
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Tap <Share className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> <span className="font-medium">Share</span>, then{" "}
              <span className="font-medium">"Add to Home Screen"</span> to install the app.
            </p>
          ) : (
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Add it to your home screen for quick, full-screen access — no app store needed.
            </p>
          )}
          {!iosHint && (
            <button
              data-testid="pwa-install-button"
              onClick={install}
              className="mt-2.5 inline-flex items-center gap-1.5 bg-amber-400 text-slate-900 hover:bg-amber-300 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Install app
            </button>
          )}
        </div>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          className="text-slate-400 hover:text-white transition-colors shrink-0"
          title="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
